import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import ErrorMessage from '@minecraft.error-message';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import { useTranslation } from '@blocs.i18n';
import { gqlListSortOrderEnum } from '@minecraft.graphql-types';
import { QUERY_REPRESENTATIVE_LISTS } from '../../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FiltersNameEnum } from '../../../shared/types';
import { FormItem } from '../../../shared/styles';
import { generateSelectOptionsBasedOnField } from '../../../shared/utils';
import { FilterDrawerType, FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { useFiltersLabel } from '../../shared/hooks/useFiltersLabel';
import { FilterTitle } from '../../shared/styles';
import { Representatives, RepresentativeList, TalentTypeAdvancedFilter } from '../../FilterDrawerCollection';

interface Props {
  isSubmissionContext: boolean;
  type: FilterDrawerType;
  roleId: number;
}

const RepresentationCategory: FC<Props> = ({ isSubmissionContext, type, roleId }) => {
  const { t } = useTranslation();
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_REPRESENTATIVE_LISTS, {
    variables: {
      input: {
        sortOrder: gqlListSortOrderEnum.ALPHABETICAL,
      },
    },
  });

  const { isSearching, isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.representation);
  const { filtersCounts } = useDrawerFiltersCounts();

  const representativeListOptions = useMemo(() => {
    return generateSelectOptionsBasedOnField(
      data?.representativeLists?.objects,
      'name',
      false,
      filtersCounts?.[FiltersCountNameEnum.representativeListIds]
    );
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (representativeListOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.representativeList]: representativeListOptions,
      });
    }
  }, [representativeListOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <FormItem $fullWidth $marginValue={2}>
        {isSearching && <FilterTitle>{t('common:filtersName.representation')}</FilterTitle>}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.talentType) && (
          <FormItem $fullWidth $marginValue={2}>
            <TalentTypeAdvancedFilter
              name={FiltersNameEnum.talentType}
              label={filtersLabelOfAudience[FiltersNameEnum.talentType]}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.representatives) && (
          <FormItem $fullWidth $marginValue={2}>
            <Representatives
              name={FiltersNameEnum.representatives}
              label={filtersLabelOfAudience[FiltersNameEnum.representatives]}
              searchByRoleSubmissionAndName={isSubmissionContext}
              roleId={roleId}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.representativeList) && (
          <FormItem $fullWidth $marginValue={2}>
            <RepresentativeList
              name={FiltersNameEnum.representativeList}
              label={filtersLabelOfAudience[FiltersNameEnum.representativeList]}
            />
          </FormItem>
        )}
      </FormItem>
    )
  );
};

export default RepresentationCategory;
